import { ChangeEvent, FC, RefObject, useEffect, useRef, useState } from 'react';

import {
  Category,
  Container,
  ContainerInner,
  Heading,
  QuestionButton,
  QuestionHeader,
  QuestionStep,
  QuestionHeaderContent,
  ButtonContainer,
  QuestionHelperText,
  LeftArrow,
} from './question.styles';
import GridQuestionLayout from './QuestionTypes/GridQuestionLayout';
import BasicQuestionLayout from './QuestionTypes/BasicQuestionLayout';
import { updatePage } from '../../redux/pagination';
import { resetPageIndex } from '../../redux/pagination';
import { resetAnswers, updateAnswers } from '../../redux/answers';
import {
  gridQuestionsLayoutWith2InLastRow,
  gridQuestionsLayoutWith3InLastRow,
} from './config';
import { Question as QuestionType, QuestionOption } from '../Questionnaire';
import { useAppDispatch, useAppSelector } from '../../store';
import useBackendPath from '../../hooks/useBackendPath';

const categoryToCategoryName = {
  strategy: 'Strategy',
  transparency: 'Transparency',
  burnLess: 'Burn less',
  burnClean: 'Burn clean',
};

const categoryToHeaderImage = {
  strategy: '/assets/images/icons/questions-pt1.svg',
  transparency: '/assets/images/icons/questions-pt2.svg',
  burnLess: '/assets/images/icons/questions-pt3.svg',
  burnClean: '/assets/images/icons/questions-pt4.svg',
};

export type HandleInputChangeType = (
  e: ChangeEvent<HTMLInputElement>,
  option: QuestionOption,
  multiChoice?: boolean,
) => void;

interface Props {
  item: QuestionType;
  index: number;
}

const Question: FC<Props> = ({ item, index }) => {
  const dispatch = useAppDispatch();
  const containerRef: RefObject<HTMLDivElement> | null | undefined =
    useRef(null);
  const pagination = useAppSelector((state) => state.pagination);
  const { selectedAnswers: selectedAnswersState } = useAppSelector(
    (state) => state.answers,
  );

  const { backendPath } = useBackendPath();

  const [selectedAnswers, setSelectedAnswers] = useState<QuestionOption[]>([]);

  const handleInputChange: HandleInputChangeType = (
    event,
    selectedOption,
    isMultiChoice,
  ) => {
    const answerStack = isMultiChoice
      ? (function () {
          const answers = [...selectedAnswers];
          const isChecked = event.currentTarget.checked;
          let result = [];
          if (!isChecked) {
            result = answers.reduce<QuestionOption[]>((arr, curr) => {
              if (curr.id !== selectedOption.id) {
                return [...arr, curr];
              } else {
                return arr;
              }
            }, []);
          } else {
            result = [...answers, selectedOption];
          }
          return result;
        })()
      : [selectedOption];
    setSelectedAnswers(answerStack);
  };

  const handleNext = () => {
    const questionData = { ...item };
    delete questionData.isMultiChoice;
    delete questionData.questionOptions;

    dispatch(
      updateAnswers({
        questionId: questionData.questionId,
        answers: selectedAnswers,
      }),
    );
    dispatch(updatePage(pagination.index + 1));
  };

  const handlePrevious = () => {
    dispatch(updatePage(pagination.index - 1));
  };

  const handleReset = () => {
    dispatch(resetPageIndex());
    dispatch(resetAnswers());
  };

  useEffect(() => {
    // When page index is reset to zero from a reset.
    if (pagination.index === 0 && selectedAnswers.length > 0) {
      setSelectedAnswers([]);
      containerRef.current?.scrollTo(0, 0);
    }
  }, [pagination.index, selectedAnswers]);

  if (index + 1 !== pagination.index) {
    return null;
  }

  const areTwoItemsInLastRow = gridQuestionsLayoutWith2InLastRow.includes(
    item.questionId,
  );
  const areThreeItemsInLastRow = gridQuestionsLayoutWith3InLastRow.includes(
    item.questionId,
  );

  return (
    <Container ref={containerRef}>
      <ContainerInner>
        <QuestionHeader>
          <img
            src={backendPath + categoryToHeaderImage[item.category]}
            alt=""
          />
          <QuestionHeaderContent>
            <Category>{categoryToCategoryName[item.category]}</Category>
            <Heading>{item.question}</Heading>
            <QuestionStep>
              {item.isMultiChoice && (
                <QuestionHelperText>Select all that apply</QuestionHelperText>
              )}
            </QuestionStep>
          </QuestionHeaderContent>
        </QuestionHeader>
        {areTwoItemsInLastRow || areThreeItemsInLastRow ? (
          <GridQuestionLayout
            item={item}
            handleInputChange={handleInputChange}
            selectedAnswers={selectedAnswers}
            numberOfItemsInLastRow={areTwoItemsInLastRow ? 2 : undefined}
          />
        ) : (
          <BasicQuestionLayout
            item={item}
            handleInputChange={handleInputChange}
            selectedAnswers={selectedAnswers}
          />
        )}
        <ButtonContainer>
          <QuestionButton
            onClick={handleReset}
            disabled={pagination.index < 2 && selectedAnswersState.length === 0}
          >
            <img
              src={`${backendPath}/assets/images/icons/refresh.svg`}
              alt=""
            />
            Restart
          </QuestionButton>
          <QuestionButton
            onClick={handlePrevious}
            disabled={pagination.index < 2}
            primary
          >
            <LeftArrow
              src={`${backendPath}/assets/images/icons/arrow.svg`}
              alt=""
            />
            Previous
          </QuestionButton>
          <QuestionButton
            onClick={handleNext}
            disabled={selectedAnswers.length === 0}
            primary
          >
            Next
            <img src={`${backendPath}/assets/images/icons/arrow.svg`} alt="" />
          </QuestionButton>
        </ButtonContainer>
      </ContainerInner>
    </Container>
  );
};

export default Question;
